import { STAT_MAX_BATCH, type StatEvent } from "@greg-chick/shared";
import { EDGE_BASE } from "./apiBase";

/**
 * **비식별 집계 보내기** — 몇 번 일어났는지만. 누구인지는 안 보낸다.
 *
 * ## 왜 모아서 한 번에 보내나
 * 일어날 때마다 보내면 **요청 수가 몇 배로 뛴다.** 지금 한 사람이 서버에 17번 묻는데
 * (docs/build/30), 집계로 그걸 두 배 만들면 무료 한도를 그만큼 빨리 태운다.
 * 그래서 방문 중에는 메모리에 쌓아 두고, **떠날 때 한 번** 보낸다.
 *
 * ## 떠나는 순간을 어떻게 잡나
 * `visibilitychange`(탭을 가리거나 앱을 내림)와 `pagehide` 를 쓴다.
 * `beforeunload` 는 모바일에서 잘 안 불린다 — 폰은 탭을 닫는 대신 앱을 내리기 때문이다.
 *
 * `sendBeacon` 은 페이지가 죽는 중에도 전송을 보장한다(fetch 는 취소된다).
 *
 * ## 🔴 그런데 「떠날 때」를 못 잡으면 한 방문이 통째로 사라진다
 * 폰이 탭을 죽이거나(메모리 부족) 브라우저가 갑자기 닫히면 `pagehide` 가 안 오고,
 * 그 방문의 **모든 숫자가 없던 일이 된다.** 실제로 확인했다 — 진짜 브라우저로 운영 화면을
 * 10곳 열었는데 집계에 **한 줄도 안 남았다**(2026-08-28).
 *
 * GA4 를 안 켠 지금 이 집계가 **유일한 지표**라 이건 그냥 넘길 일이 아니다.
 * 그래서 **처음 쌓인 뒤 잠깐 있다가 한 번은 확실히 보낸다**(`FIRST_FLUSH_MS`).
 * 한 방문에 요청이 **한 번** 느는 대신, 「왔다」는 사실은 잃지 않는다.
 *
 * ## 실패해도 아무 일 없다
 * 통계는 부가 기능이다. 못 보내면 그냥 사라진다 — 다시 시도하지 않고, 화면도 막지 않는다.
 */
const queue: { name: StatEvent; key?: string }[] = [];
let armed = false;
/**
 * 처음 쌓인 뒤 이만큼 지나면 **한 번은 보낸다.**
 *
 * 20초인 이유: 첫 화면을 보고 바로 닫는 사람도 「왔다」는 남기고, 계속 노는 아이에겐
 * 요청이 한 번만 더 는다. 더 짧으면 한 방문에 여러 번 보내게 되고, 더 길면
 * 짧게 보고 떠난 방문을 그대로 놓친다.
 */
export const FIRST_FLUSH_MS = 20_000;
let firstTimer: ReturnType<typeof setTimeout> | null = null;
/** 이번 방문에서 시간으로 한 번 보냈나 — **한 방문에 한 번만** 늘리려고 붙든다. */
let firstFlushDone = false;

/** 셀 것 하나 추가. 화면을 막지 않는다(그냥 메모리에 쌓는다). */
export function countStat(name: StatEvent, key?: string): void {
  if (queue.length >= STAT_MAX_BATCH) return; // 한 방문에서 과하게 쌓이면 버린다
  queue.push(key ? { name, key } : { name });
  arm();
}

function arm(): void {
  // 🔴 **처음 한 번은 시간으로도 보낸다** — 떠나는 순간을 못 잡을 때의 보험(위 주석).
  //    타이머는 한 방문에 하나만 건다(보낸 뒤 다시 쌓여도 새로 걸지 않는다).
  if (!firstFlushDone && firstTimer === null && typeof setTimeout === "function") {
    firstTimer = setTimeout(flush, FIRST_FLUSH_MS);
  }
  if (armed || typeof document === "undefined") return;
  armed = true;
  const flushIfHidden = () => {
    if (document.visibilityState === "hidden") flush();
  };
  document.addEventListener("visibilitychange", flushIfHidden);
  window.addEventListener("pagehide", flush);
}

/** 쌓인 것을 보낸다. 보낸 뒤 비운다(두 번 세지 않게). */
export function flush(): void {
  // 타이머로 왔든 떠나면서 왔든, 예약된 것은 지운다 — 빈 채로 또 부르지 않게
  if (firstTimer !== null) {
    clearTimeout(firstTimer);
    firstTimer = null;
  }
  firstFlushDone = true;
  if (queue.length === 0) return;
  const hits = queue.splice(0, queue.length);
  const url = `${EDGE_BASE}/api/stat`;
  const body = JSON.stringify({ hits });
  try {
    /**
     * 🔴 **엣지가 다른 출처면 sendBeacon 을 안 쓴다**(2026-10-10). sendBeacon 은 늘 자격 증명을 싣는데,
     * 엣지 CORS 는 출처를 되돌려 줄 뿐 자격 증명은 안 연다(열면 흔한 CORS 설정 실수가 된다).
     * 그래서 개발(웹 :5173 → 엣지 :8788)에서는 사파리가 「Credentials flag is true…」로 막고 집계가 버려졌다 —
     * e2e 가 로컬 엣지를 켠 날만 빨개졌다. 운영은 웹과 엣지가 같은 출처라 그대로 sendBeacon 이다.
     */
    const sameOrigin = new URL(url, location.href).origin === location.origin;
    if (!sameOrigin) {
      void fetch(url, { method: "POST", body, keepalive: true, credentials: "omit", headers: { "content-type": "application/json" } }).catch(
        () => undefined,
      );
      return;
    }
    if (navigator.sendBeacon) {
      // Blob 으로 보내야 content-type 이 붙는다(엣지가 JSON 으로 읽는다)
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
    // sendBeacon 이 없는 옛 브라우저 — keepalive 로 최선을 다한다
    void fetch(url, { method: "POST", body, keepalive: true, headers: { "content-type": "application/json" } }).catch(
      () => undefined,
    );
  } catch {
    // 전송 실패는 그냥 버린다 — 통계 때문에 화면이 흔들리면 안 된다
  }
}

/** 검사용 — 지금 몇 개 쌓였나. */
export function pendingStats(): number {
  return queue.length;
}

/** 검사용 — 한 방문 상태를 되돌린다. 운영에서는 부르지 않는다. */
export function resetStatForTest(): void {
  queue.length = 0;
  armed = false;
  if (firstTimer !== null) clearTimeout(firstTimer);
  firstTimer = null;
  firstFlushDone = false;
}
